// frontend/src/modules/email/pages/CampaignsTab.tsx
//
// Email campaigns (Round 2 Tier 3): bulk send + scheduling. List ↔ editor ↔
// detail live inside the tab. The editor is markdown-with-live-preview
// (the preview uses the campaign renderer — a mirror of the backend's escaped
// email-safe subset — so it matches the send output). Sending happens in cron
// batches — the detail view surfaces per-recipient delivery state.

import * as React from 'react';
import { toast } from 'sonner';
import { ArrowLeft, Megaphone, Plus, RefreshCw, Send, Trash2, XCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { formatDateTime } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { renderCampaignMarkdown } from '../campaign-markdown';
import { emailApi } from '../api';
import type {
  EmailCampaign, EmailCampaignListItem, EmailCampaignRecipient,
  CampaignAudience, CampaignStatus, CampaignRecipientStatus, SelectedRecipient,
} from '../types';

type View =
  | { mode: 'list' }
  | { mode: 'edit'; campaign: EmailCampaign | null }
  | { mode: 'detail'; id: string };

export function CampaignsTab() {
  const [view, setView] = React.useState<View>({ mode: 'list' });

  if (view.mode === 'edit') {
    return (
      <CampaignEditor
        campaign={view.campaign}
        onBack={() => setView({ mode: 'list' })}
        onSaved={(c) => setView({ mode: 'detail', id: c.id })}
      />
    );
  }
  if (view.mode === 'detail') {
    return (
      <CampaignDetail
        id={view.id}
        onBack={() => setView({ mode: 'list' })}
        onEdit={(c) => setView({ mode: 'edit', campaign: c })}
      />
    );
  }
  return (
    <CampaignList
      onNew={() => setView({ mode: 'edit', campaign: null })}
      onOpen={(id) => setView({ mode: 'detail', id })}
    />
  );
}

// ─── Status pills ─────────────────────────────────────────────────────────────

const CAMPAIGN_TONE: Record<CampaignStatus, Parameters<typeof statusPill>[0]> = {
  draft: 'info', scheduled: 'warning', sending: 'warning', sent: 'success', cancelled: 'destructive',
};
const CAMPAIGN_LABEL: Record<CampaignStatus, string> = {
  draft: 'Draft', scheduled: 'Scheduled', sending: 'Sending', sent: 'Sent', cancelled: 'Cancelled',
};

function campaignBadge(status: CampaignStatus): React.ReactNode {
  const tone = CAMPAIGN_TONE[status];
  return <span className={statusPill(tone)}><span className={statusDot(tone)} />{CAMPAIGN_LABEL[status]}</span>;
}

const RECIPIENT_TONE: Record<CampaignRecipientStatus, Parameters<typeof statusPill>[0]> = {
  pending: 'info', sent: 'success', failed: 'destructive', suppressed: 'warning',
};

function recipientBadge(status: CampaignRecipientStatus): React.ReactNode {
  const tone = RECIPIENT_TONE[status];
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return <span className={statusPill(tone)}><span className={statusDot(tone)} />{label}</span>;
}

// ─── List ─────────────────────────────────────────────────────────────────────

function CampaignList({ onNew, onOpen }: { onNew: () => void; onOpen: (id: string) => void }) {
  const [rows, setRows] = React.useState<EmailCampaignListItem[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    emailApi.listCampaigns()
      .then(setRows)
      .catch((err: any) => toast.error('Could not load campaigns', { description: err.message }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[12.5px] text-muted-foreground">
          One-off announcements and newsletters to your clients. Drafts can be sent immediately
          or scheduled; delivery runs in batches and respects your suppression list.
        </p>
        <Button onClick={onNew} className="rounded-full h-10 px-5 text-[13px] font-semibold shrink-0 ml-4">
          <Plus className="size-4 mr-1.5" />
          New campaign
        </Button>
      </div>
      <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
        <DataTable<EmailCampaignListItem>
          loading={loading}
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={(r) => onOpen(r.id)}
          columns={[
            { key: 'name',    header: 'Campaign', cell: (r) => <span className="font-semibold text-foreground">{r.name}</span> },
            { key: 'status',  header: 'Status',   cell: (r) => campaignBadge(r.status) },
            { key: 'aud',     header: 'Audience', cell: (r) => r.audience === 'all_clients' ? 'All clients' : 'Selected emails', className: 'text-muted-foreground' },
            {
              key: 'recips', header: 'Recipients',
              cell: (r) => r.recipients.total === 0
                ? <span className="text-muted-foreground">—</span>
                : <span>{r.recipients.sent}/{r.recipients.total} sent{r.recipients.failed > 0 && <span className="text-destructive"> · {r.recipients.failed} failed</span>}</span>,
            },
            {
              key: 'when', header: 'When',
              cell: (r) => r.completed_at ? `Sent ${formatDateTime(r.completed_at)}`
                : r.scheduled_at ? `Scheduled ${formatDateTime(r.scheduled_at)}`
                : `Created ${formatDateTime(r.created_at)}`,
              className: 'text-muted-foreground',
            },
          ]}
          emptyState={
            <EmptyState
              icon={<Megaphone />}
              title="No campaigns yet"
              description="Create your first campaign to email all your clients at once — announcements, updates, or newsletters."
            />
          }
        />
      </section>
    </div>
  );
}

// ─── Editor ───────────────────────────────────────────────────────────────────

const DEFAULT_BODY = `Hi {{name}},

We have some news to share with you.

**What changed:** tell your clients here. You can use *italics*, **bold**, and [links](https://example.com).

Thanks,
The team`;

/** Parse a "selected emails" textarea: one entry per line, `email` or `Name <email>`. */
function parseSelectedEmails(raw: string): SelectedRecipient[] {
  const out: SelectedRecipient[] = [];
  for (const line of raw.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const withName = trimmed.match(/^(.*?)\s*<([^<>\s]+@[^<>\s]+)>$/);
    if (withName) out.push({ email: withName[2], name: withName[1].trim() || null });
    else out.push({ email: trimmed });
  }
  return out;
}

function CampaignEditor({ campaign, onBack, onSaved }: {
  campaign: EmailCampaign | null;
  onBack: () => void;
  onSaved: (c: EmailCampaign) => void;
}) {
  const [name, setName] = React.useState(campaign?.name ?? '');
  const [subject, setSubject] = React.useState(campaign?.subject ?? '');
  const [body, setBody] = React.useState(campaign?.body_markdown ?? DEFAULT_BODY);
  const [audience, setAudience] = React.useState<CampaignAudience>(campaign?.audience ?? 'all_clients');
  const [selectedRaw, setSelectedRaw] = React.useState('');
  const [scheduleAt, setScheduleAt] = React.useState('');
  const [busy, setBusy] = React.useState<null | 'save' | 'send' | 'schedule'>(null);
  const [audienceCount, setAudienceCount] = React.useState<number | null>(null);

  React.useEffect(() => {
    emailApi.campaignAudiencePreview()
      .then((p) => setAudienceCount(p.all_clients))
      .catch(() => setAudienceCount(null));
  }, []);

  // Editing a selected-audience campaign: preload the persisted recipient list
  // (saved with the draft / materialized at schedule time) so the user never
  // has to re-paste it. Skipped once the user starts typing their own list.
  const preloadedRef = React.useRef(false);
  React.useEffect(() => {
    if (!campaign || campaign.audience !== 'selected' || preloadedRef.current) return;
    let cancelled = false;
    emailApi.getCampaign(campaign.id)
      .then((d) => {
        if (cancelled || preloadedRef.current) return;
        preloadedRef.current = true;
        const lines = d.recipients.map((r) => (r.name ? `${r.name} <${r.email}>` : r.email));
        if (lines.length > 0) {
          setSelectedRaw((current) => (current.trim() ? current : lines.join('\n')));
        }
      })
      .catch(() => { /* best-effort — the user can still paste the list */ });
    return () => { cancelled = true; };
  }, [campaign]);

  // Use the campaign renderer (mirror of the backend's minimal subset) so the
  // preview matches the send output exactly — not the richer marketing
  // renderMarkdown, which would render headings/lists/images recipients won't get.
  const previewHtml = React.useMemo(
    () => renderCampaignMarkdown(body.replace(/\{\{\s*name\s*\}\}/g, 'there')),
    [body],
  );

  const valid = name.trim() && subject.trim() && body.trim()
    && (audience === 'all_clients' || selectedRaw.trim());

  function selectedPayload(): SelectedRecipient[] | null {
    return audience === 'selected' ? parseSelectedEmails(selectedRaw) : null;
  }

  /** Create or update the draft (recipient list included so Save-draft
   *  persists it — no re-pasting on later edits); returns the saved campaign. */
  async function persist(): Promise<EmailCampaign> {
    const input = { name: name.trim(), subject: subject.trim(), body_markdown: body, audience };
    const emails = selectedPayload();
    return campaign
      ? emailApi.updateCampaign(campaign.id, input, emails)
      : emailApi.createCampaign(input, emails);
  }

  async function saveDraft() {
    setBusy('save');
    try {
      const saved = await persist();
      toast.success('Draft saved');
      onSaved(saved);
    } catch (err: any) {
      toast.error('Could not save', { description: err.message });
    } finally { setBusy(null); }
  }

  async function sendNow() {
    setBusy('send');
    try {
      const saved = await persist();
      const launched = await emailApi.sendCampaignNow(saved.id, selectedPayload());
      toast.success('Campaign queued — sending starts on the next batch run');
      onSaved(launched);
    } catch (err: any) {
      toast.error('Could not send', { description: err.message });
    } finally { setBusy(null); }
  }

  async function schedule() {
    if (!scheduleAt) { toast.error('Pick a date and time first'); return; }
    setBusy('schedule');
    try {
      const saved = await persist();
      const launched = await emailApi.scheduleCampaign(
        saved.id, new Date(scheduleAt).toISOString(), selectedPayload(),
      );
      toast.success('Campaign scheduled');
      onSaved(launched);
    } catch (err: any) {
      toast.error('Could not schedule', { description: err.message });
    } finally { setBusy(null); }
  }

  return (
    <div className="max-w-6xl space-y-5">
      <Button variant="ghost" size="sm" onClick={onBack} className="rounded-full h-8 -ml-2 text-muted-foreground">
        <ArrowLeft className="size-3.5 mr-1.5" />
        Back to campaigns
      </Button>

      <section className={[VISUAL.bento, 'p-6 sm:p-7 space-y-5'].join(' ')}>
        <h3 className={VISUAL.dashSection}>{campaign ? 'Edit campaign' : 'New campaign'}</h3>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="grid gap-1.5">
            <Label>Internal name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="June product update" />
          </div>
          <div className="grid gap-1.5">
            <Label>Subject line</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Hi {{name}} — what's new this month" />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="grid gap-1.5">
            <div className="flex items-baseline justify-between">
              <Label>Body (markdown)</Label>
              <span className="text-[11.5px] text-muted-foreground">
                <code className="font-mono bg-muted/60 px-1 py-0.5 rounded">{'{{name}}'}</code> inserts the recipient's name ("there" if unknown)
              </span>
            </div>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={16}
              className="font-mono text-[12.5px] leading-relaxed resize-y"
            />
            <p className="text-[11.5px] text-muted-foreground">
              Supports paragraphs, **bold**, *italics*, and [links](https://…). Raw HTML is never sent.
            </p>
          </div>
          <div className="grid gap-1.5">
            <Label>Live preview</Label>
            <div className="rounded-xl ring-1 ring-border/40 bg-background p-5 min-h-[200px] overflow-auto">
              <div className="text-[13px] font-semibold text-foreground mb-3 pb-3 border-b border-border/40">
                {subject.replace(/\{\{\s*name\s*\}\}/g, 'there') || <span className="text-muted-foreground">Subject preview</span>}
              </div>
              {/* renderCampaignMarkdown escapes all raw HTML before re-introducing its whitelist */}
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </div>
        </div>

        <div className="grid gap-3">
          <Label>Audience</Label>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => setAudience('all_clients')}
              className={[
                'flex-1 text-left rounded-xl ring-1 p-4 transition-colors',
                audience === 'all_clients' ? 'ring-foreground/60 bg-muted/40' : 'ring-border/40 hover:bg-muted/20',
              ].join(' ')}
            >
              <div className="text-[13.5px] font-semibold text-foreground">All clients</div>
              <div className="text-[12px] text-muted-foreground mt-0.5">
                {audienceCount == null ? 'Every active client with an email address'
                  : `${audienceCount} active client${audienceCount === 1 ? '' : 's'} with an email address`}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setAudience('selected')}
              className={[
                'flex-1 text-left rounded-xl ring-1 p-4 transition-colors',
                audience === 'selected' ? 'ring-foreground/60 bg-muted/40' : 'ring-border/40 hover:bg-muted/20',
              ].join(' ')}
            >
              <div className="text-[13.5px] font-semibold text-foreground">Selected emails</div>
              <div className="text-[12px] text-muted-foreground mt-0.5">Paste a specific list (up to 1,000)</div>
            </button>
          </div>
          {audience === 'selected' && (
            <div className="grid gap-1.5">
              <Textarea
                value={selectedRaw}
                onChange={(e) => setSelectedRaw(e.target.value)}
                rows={5}
                placeholder={'one per line:\nclient@example.com\nJane Doe <jane@example.com>'}
                className="font-mono text-[12.5px]"
              />
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-border/40">
          <Button variant="outline" onClick={saveDraft} disabled={!valid || busy !== null} className="rounded-full h-10 px-5 text-[13px] font-semibold">
            {busy === 'save' ? 'Saving…' : 'Save draft'}
          </Button>
          <Button onClick={sendNow} disabled={!valid || busy !== null} className="rounded-full h-10 px-5 text-[13px] font-semibold">
            <Send className="size-3.5 mr-1.5" />
            {busy === 'send' ? 'Queuing…' : 'Send now'}
          </Button>
          <div className="flex items-end gap-2">
            <div className="grid gap-1.5">
              <Label className="text-[11.5px]">Or schedule for</Label>
              <Input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                className="h-10 w-[210px]"
              />
            </div>
            <Button variant="outline" onClick={schedule} disabled={!valid || !scheduleAt || busy !== null} className="rounded-full h-10 px-5 text-[13px] font-semibold">
              {busy === 'schedule' ? 'Scheduling…' : 'Schedule'}
            </Button>
          </div>
        </div>
        <p className="text-[11.5px] text-muted-foreground">
          Delivery runs in batches of 50 on the campaign scheduler, so large sends complete over
          multiple runs. Suppressed (bounced/complained) addresses are skipped automatically.
        </p>
      </section>
    </div>
  );
}

// ─── Detail ───────────────────────────────────────────────────────────────────

function CampaignDetail({ id, onBack, onEdit }: {
  id: string;
  onBack: () => void;
  onEdit: (c: EmailCampaign) => void;
}) {
  const [campaign, setCampaign] = React.useState<EmailCampaignListItem | null>(null);
  const [recipients, setRecipients] = React.useState<EmailCampaignRecipient[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    emailApi.getCampaign(id)
      .then((d) => { setCampaign(d.campaign); setRecipients(d.recipients); })
      .catch((err: any) => toast.error('Could not load campaign', { description: err.message }))
      .finally(() => setLoading(false));
  }, [id]);

  React.useEffect(() => { load(); }, [load]);

  async function cancel() {
    setBusy(true);
    try {
      await emailApi.cancelCampaign(id);
      toast.success('Campaign cancelled');
      load();
    } catch (err: any) {
      toast.error('Could not cancel', { description: err.message });
    } finally { setBusy(false); }
  }

  async function remove() {
    // A sent campaign's recipient rows are personal data of non-users, so say
    // plainly what is being destroyed rather than reusing the draft wording.
    if (campaign?.status !== 'draft' && !window.confirm(
      'Delete this campaign and its recipient list permanently? '
      + 'The per-recipient delivery history will be erased and cannot be recovered.'
    )) return;
    setBusy(true);
    try {
      await emailApi.deleteCampaign(id);
      toast.success(campaign?.status === 'draft' ? 'Draft deleted' : 'Campaign and recipient list deleted');
      onBack();
    } catch (err: any) {
      toast.error('Could not delete', { description: err.message });
      setBusy(false);
    }
  }

  async function sendNow() {
    setBusy(true);
    try {
      await emailApi.sendCampaignNow(id, null);
      toast.success('Campaign queued — sending starts on the next batch run');
      load();
    } catch (err: any) {
      toast.error('Could not send', { description: err.message });
    } finally { setBusy(false); }
  }

  if (loading) return <LoadingState />;
  if (!campaign) {
    return (
      <div className="max-w-3xl">
        <EmptyState icon={<Megaphone />} title="Campaign not found" description="It may have been deleted." />
        <Button variant="outline" onClick={onBack} className="rounded-full h-9 mt-4">Back</Button>
      </div>
    );
  }

  const editable = campaign.status === 'draft' || campaign.status === 'scheduled';
  const cancellable = campaign.status === 'scheduled' || campaign.status === 'sending';
  const stats = campaign.recipients;

  return (
    <div className="max-w-6xl space-y-5">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} className="rounded-full h-8 -ml-2 text-muted-foreground">
          <ArrowLeft className="size-3.5 mr-1.5" />
          Back to campaigns
        </Button>
        <Button variant="ghost" size="sm" onClick={load} className="rounded-full h-8 text-muted-foreground" aria-label="Refresh">
          <RefreshCw className="size-3.5 mr-1.5" />
          Refresh
        </Button>
      </div>

      <section className={[VISUAL.bento, 'p-6 sm:p-7 space-y-5'].join(' ')}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h3 className="text-[17px] font-semibold text-foreground">{campaign.name}</h3>
            {campaignBadge(campaign.status)}
          </div>
          <div className="flex items-center gap-2">
            {editable && (
              <>
                <Button size="sm" variant="outline" onClick={() => onEdit(campaign)} disabled={busy} className="rounded-full h-9">Edit</Button>
                {/* Selected-audience campaigns can send-now too: the backend
                    reuses the persisted/materialized recipient list. */}
                {(campaign.audience === 'all_clients' || stats.total > 0) && (
                  <Button size="sm" onClick={sendNow} disabled={busy} className="rounded-full h-9">
                    <Send className="size-3.5 mr-1.5" />Send now
                  </Button>
                )}
              </>
            )}
            {cancellable && (
              <Button size="sm" variant="outline" onClick={cancel} disabled={busy} className="rounded-full h-9">
                <XCircle className="size-3.5 mr-1.5" />Cancel
              </Button>
            )}
            {/* Deletable = draft | sent | cancelled, matching the API. A SENT
                campaign must be deletable: its recipient rows hold the name and
                email address of people who are not users of this workspace, and
                erasing them on request is the only way to honour that request.
                'scheduled' and 'sending' are excluded because deleting mid-send
                races the campaigns cron — cancel first, then delete. */}
            {(campaign.status === 'draft' || campaign.status === 'sent' || campaign.status === 'cancelled') && (
              <Button size="sm" variant="outline" onClick={remove} disabled={busy} className="rounded-full h-9 text-destructive">
                <Trash2 className="size-3.5 mr-1.5" />Delete
              </Button>
            )}
          </div>
        </div>

        <div className="text-[13px] text-foreground/85">
          <span className="text-muted-foreground">Subject: </span>{campaign.subject}
        </div>

        {/* Status timeline */}
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-[12.5px] text-muted-foreground">
          <span>Created {formatDateTime(campaign.created_at)}</span>
          {campaign.scheduled_at && <span>· Scheduled for {formatDateTime(campaign.scheduled_at)}</span>}
          {campaign.started_at && <span>· Started {formatDateTime(campaign.started_at)}</span>}
          {campaign.completed_at && <span>· Completed {formatDateTime(campaign.completed_at)}</span>}
        </div>

        {stats.total > 0 && (
          <div className="flex flex-wrap gap-2">
            <span className={statusPill('info')}><span className={statusDot('info')} />{stats.pending} pending</span>
            <span className={statusPill('success')}><span className={statusDot('success')} />{stats.sent} sent</span>
            <span className={statusPill('destructive')}><span className={statusDot('destructive')} />{stats.failed} failed</span>
            <span className={statusPill('warning')}><span className={statusDot('warning')} />{stats.suppressed} suppressed</span>
          </div>
        )}
        {campaign.status === 'sending' && stats.pending > 0 && (
          <p className="text-[12px] text-muted-foreground">
            Sending in batches of 50 per scheduler run — refresh to see progress.
          </p>
        )}
      </section>

      <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
        <DataTable<EmailCampaignRecipient>
          rows={recipients}
          rowKey={(r) => r.id}
          columns={[
            { key: 'email',  header: 'Email',  cell: (r) => <span className="font-medium">{r.email}</span> },
            { key: 'name',   header: 'Name',   cell: (r) => r.name ?? <span className="text-muted-foreground">—</span> },
            { key: 'status', header: 'Status', cell: (r) => recipientBadge(r.status) },
            {
              key: 'detail', header: 'Detail',
              cell: (r) => r.status === 'failed' && r.error
                ? <span className="text-xs text-muted-foreground truncate inline-block max-w-xs">{r.error}</span>
                : r.sent_at ? <span className="text-muted-foreground">{formatDateTime(r.sent_at)}</span>
                : <span className="text-muted-foreground">—</span>,
            },
          ]}
          emptyState={
            <EmptyState
              icon={<Megaphone />}
              title="No recipients yet"
              description="Recipients are locked in when you send or schedule the campaign."
            />
          }
        />
      </section>
    </div>
  );
}
